import { fieldLength, fieldRequired, type Field } from "@ondit/publishing";
import { Input, Label, Textarea } from "@ondit/ui";
import { type ReactNode, useId } from "react";
import { WritingField } from "~/common/article/writing-field";
import { pick, useI18n, useT } from "~/core/i18n";
import { Hint } from "~/core/ui/page";
import { SearchSelect } from "~/core/ui/search-select";
/** Shared platform field controls; the caller supplies its authorized media picker. */
export function PublishingField({
	field: f,
	fields,
	disabled,
	onChange,
	onBlur,
	invalid = false,
	feedback,
	imageControl,
}: {
	field: Field;
	fields: Record<string, string>;
	disabled: boolean;
	onChange: (value: string) => void;
	onBlur?: () => void;
	invalid?: boolean;
	feedback?: ReactNode;
	imageControl?: ReactNode;
}) {
	const id = useId();
	const { locale } = useI18n();
	const t = useT();
	const value = fields[f.key] ?? "";
	const length = fieldLength(f, value);
	const feedbackId = `${id}-${f.key}-feedback`;
	return (
		<div className="field" key={f.key} data-publish-field={f.key}>
			<div className="publisher-field-label">
				<Label htmlFor={`${id}-${f.key}`}>
					{pick(locale, f.label)}
					{fieldRequired(f, fields) ? " *" : ""}
				</Label>
				<Hint text={t(f.transfer === "guided" ? "publishing.platform.guided" : "publishing.platform.automatic")} />
				{f.max_length != null && (
					<span className="publisher-count">
						{length} / {f.max_length}
					</span>
				)}
			</div>
			{f.kind === "select" ? (
				<SearchSelect
					label={pick(locale, f.label)}
					placeholder={t("publishing.platform.required")}
					searchLabel={pick(locale, f.label)}
					emptyLabel={t("table.noMatch")}
					disabled={disabled}
					value={value}
					onChange={(v) => {
						onChange(v);
						onBlur?.();
					}}
					options={(f.options ?? []).map((v) => ({ value: v, label: pick(locale, f.option_labels?.[v]) ?? v }))}
				/>
			) : f.kind === "image" ? (
				imageControl
			) : f.kind === "textarea" && (f.format === "markdown" || f.format === "rich_text") ? (
				<fieldset
					id={`${id}-${f.key}`}
					className="bare-fieldset publisher-body-editor"
					data-invalid={invalid || undefined}
					aria-describedby={feedbackId}
					onBlur={() => onBlur?.()}
				>
					<WritingField
						article
						label={pick(locale, f.label)}
						value={value}
						onChange={(v) => onChange(v)}
						disabled={disabled}
					/>
				</fieldset>
			) : f.kind === "textarea" ? (
				<Textarea
					disabled={disabled}
					id={`${id}-${f.key}`}
					onBlur={() => onBlur?.()}
					rows={f.key === "body" ? 12 : 3}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					aria-invalid={invalid}
					aria-describedby={feedbackId}
				/>
			) : (
				<Input
					disabled={disabled}
					id={`${id}-${f.key}`}
					onBlur={() => onBlur?.()}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					aria-invalid={invalid}
					aria-describedby={feedbackId}
				/>
			)}
			<div
				id={feedbackId}
				className={`publisher-field-feedback${invalid ? " publisher-error" : " hint"}`}
				aria-live="polite"
			>
				{invalid && <span>{feedback}</span>}
			</div>
		</div>
	);
}
